import { useEffect, useRef, useState } from "react";
import { features } from "@/mocks/content";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

export default function FeaturesSection() {
  const { ref, inView } = useInView(0.1);

  return (
    <section
      id="features"
      className="relative py-20 md:py-32 overflow-hidden"
      style={{ background: "#0D0D0D" }}
    >
      {/* Background accent */}
      <div
        className="absolute top-0 right-0 w-1/2 h-full opacity-5"
        style={{
          background:
            "radial-gradient(ellipse at 80% 50%, #D4A373 0%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className="flex flex-col lg:flex-row gap-12 lg:gap-0 items-start"
        >
          {/* Left: Image */}
          <div
            className="w-full lg:w-[48%] relative"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(-40px)",
              transition: "all 1s 0.1s cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          >
            <div
              className="relative overflow-hidden"
              style={{
                paddingBottom: "130%",
                boxShadow: inView
                  ? "30px 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,163,115,0.1)"
                  : "0 0 0 rgba(0,0,0,0)",
                transition: "box-shadow 1.2s 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
              }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20private%20men%20spa%20treatment%20room%20interior%20with%20dark%20stone%20walls%2C%20warm%20amber%20candle%20lighting%2C%20white%20treatment%20bed%20with%20crisp%20linen%2C%20minimalist%20japanese%20zen%20aesthetic%2C%20deep%20shadows%2C%20gold%20accent%20details%2C%20serene%20and%20exclusive%20atmosphere%2C%20vertical%20composition&width=600&height=800&seq=feature1&orientation=portrait"
                alt="プライベート施術室"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top"
                style={{
                  filter: inView ? "brightness(0.8)" : "brightness(0.4)",
                  transition: "filter 1.2s 0.3s ease",
                }}
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>

            {/* Floating accent card */}
            <div
              className="absolute -bottom-6 -right-4 md:-right-8 bg-[#1A1A1A] border border-[#D4A373]/20 p-5 md:p-6"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(20px)",
                transition: "all 0.8s 0.8s cubic-bezier(0.23, 1, 0.32, 1)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
            >
              <div
                className="text-[#D4A373] text-3xl font-light mb-1"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                5
              </div>
              <div
                className="text-white/60 text-xs tracking-widest"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                完全個室
              </div>
            </div>
          </div>

          {/* Right: Text Content */}
          <div className="w-full lg:w-[52%] lg:pl-16 xl:pl-24">
            {/* Section Header */}
            <div
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateX(0)" : "translateX(30px)",
                transition: "all 0.8s 0.2s cubic-bezier(0.23, 1, 0.32, 1)",
              }}
            >
              <div className="flex items-center gap-4 mb-4">
                <span className="w-12 h-px bg-[#D4A373]" />
                <span
                  className="text-[#D4A373] text-xs tracking-[0.4em] uppercase"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  Our Commitment
                </span>
              </div>
              <h2
                className="text-white mb-10"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)",
                  letterSpacing: "0.05em",
                }}
              >
                当サロンの<br />こだわり
              </h2>
            </div>

            {/* Feature Items */}
            <div className="space-y-0">
              {features.map((feature, i) => (
                <div
                  key={feature.number}
                  className="relative py-8 border-b border-white/8"
                  style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? "translateX(0)" : "translateX(30px)",
                    transition: `all 0.7s ${0.35 + i * 0.15}s cubic-bezier(0.23, 1, 0.32, 1)`,
                  }}
                >
                  {/* Background number */}
                  <span
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-white select-none pointer-events-none"
                    style={{
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontSize: "5rem",
                      fontWeight: 700,
                      opacity: 0.04,
                      lineHeight: 1,
                    }}
                  >
                    {feature.number}
                  </span>

                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className="text-[#D4A373] text-xs tracking-widest"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {feature.number}
                      </span>
                      <span className="w-6 h-px bg-[#D4A373]/40" />
                    </div>
                    <h3
                      className="text-white text-lg mb-3 font-medium"
                      style={{
                        fontFamily: "'Noto Serif JP', serif",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className="text-white/50 text-sm leading-relaxed"
                      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                    >
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
